'use client';

/**
 * Live status while Tessa dials/interviews (Wave A3). Polls the token-gated
 * status read — 2.5s while the call is young, 5s after — and stops on any
 * terminal state. The draft_ready state IS the conversion moment: the claim
 * CTA appears right where the visitor is already looking.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Loader2, PhoneCall, PhoneMissed } from 'lucide-react';
import type { HeroCtaCopy } from '@/lib/onboarding/hero-cta-copy';

const FAST_POLL_MS = 2500;
const SLOW_POLL_MS = 5000;
const FAST_POLLS = 12; // ~30s of fast polling, then relax
const MAX_POLL_MS = 12 * 60 * 1000; // stop after 12 min no matter what
// Nothing server-side ever writes 'no_answer' (no sweeper) — after this long
// still in queued/dialing the call is definitively dead (dial timeout +
// voicemail both resolve in well under a minute), so the UI flips itself.
const STALL_UNANSWERED_MS = 3 * 60 * 1000;
// The interview itself can legitimately run ~8 minutes ('answered' is live
// the whole time), so the answered→draft verdict gets a wider window. Past
// it (agent tool failed, call cut short) the card must NOT spin forever —
// Shane's 2026-08-17 test froze exactly here.
const STALL_ANSWERED_MS = 10 * 60 * 1000;

export function OnboardingCallStatus({
  callId,
  token,
  copy,
}: {
  callId: string;
  token: string;
  copy: HeroCtaCopy['status'];
}) {
  const [status, setStatus] = useState<string>('dialing');
  const [claimReady, setClaimReady] = useState(false);
  const pollCount = useRef(0);
  const startedAt = useRef(Date.now());
  const answeredSeenAt = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt.current > MAX_POLL_MS) {
        // Never freeze silently: whatever we're still waiting on at the hard
        // cap resolves to a fallback the visitor can act on.
        setStatus((s) => (s === 'answered' ? 'draft_missing' : s === 'queued' || s === 'dialing' ? 'no_answer' : s));
        return;
      }
      try {
        const res = await fetch(
          `/api/public/onboarding-call/${encodeURIComponent(callId)}?t=${encodeURIComponent(token)}`,
          { cache: 'no-store' },
        );
        if (res.ok) {
          const data = (await res.json()) as { status?: string; claimReady?: boolean };
          if (!cancelled && data.status) {
            if (
              ['queued', 'dialing'].includes(data.status) &&
              Date.now() - startedAt.current > STALL_UNANSWERED_MS
            ) {
              setStatus('no_answer'); // client-side verdict — see STALL note
              return;
            }
            if (data.status === 'answered' && !data.claimReady) {
              answeredSeenAt.current ??= Date.now();
              if (Date.now() - answeredSeenAt.current > STALL_ANSWERED_MS) {
                setStatus('draft_missing'); // call happened, draft never landed
                return;
              }
            }
            setStatus(data.status);
            setClaimReady(!!data.claimReady);
            if (['draft_ready', 'completed', 'no_answer', 'failed', 'expired', 'consumed'].includes(data.status)) {
              return; // terminal — stop polling
            }
          }
        }
      } catch {
        // transient — keep polling
      }
      pollCount.current += 1;
      timer = setTimeout(poll, pollCount.current < FAST_POLLS ? FAST_POLL_MS : SLOW_POLL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [callId, token]);

  if (claimReady || status === 'draft_ready') {
    return (
      <div className="flex flex-col gap-2 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {copy.drafted}
        </p>
        <Link
          href={`/claim/${callId}?t=${encodeURIComponent(token)}`}
          className="inline-flex w-fit items-center justify-center rounded-full bg-[#EF4444] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#DC2626] transition-all"
        >
          {copy.seeDraft}
        </Link>
        <p className="text-xs text-emerald-800 dark:text-emerald-200">
          {copy.textedLink}
        </p>
      </div>
    );
  }

  if (status === 'draft_missing') {
    // The call happened but the draft never landed (agent tool failure or a
    // cut-short interview). Warm dead-end escape — never an eternal spinner.
    return (
      <div className="flex items-start gap-2 rounded-2xl border p-4 text-sm text-muted-foreground">
        <PhoneMissed className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {copy.draftMissing.before}
          <Link href="/routines/new" className="underline">
            {copy.draftMissing.link}
          </Link>
          {copy.draftMissing.after}
        </span>
      </div>
    );
  }

  if (status === 'no_answer' || status === 'failed' || status === 'expired') {
    return (
      <div className="flex items-start gap-2 rounded-2xl border p-4 text-sm text-muted-foreground">
        <PhoneMissed className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {status === 'no_answer' ? copy.noAnswer : copy.failed}
          {status !== 'no_answer' && (
            <Link href="/routines/new" className="underline">
              {copy.failedLink}
            </Link>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-2xl border p-4 text-sm">
      {status === 'answered' ? (
        <>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#10B981]" />
          <span>
            {copy.answered}
          </span>
        </>
      ) : (
        <>
          <PhoneCall className="h-4 w-4 shrink-0 animate-pulse text-[#EF4444]" />
          <span>{copy.ringing}</span>
        </>
      )}
    </div>
  );
}
